import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { useBulkUpdateInvoices } from '../mutations'
import type { ApInvoiceAssignedUser, ApInvoiceBulkUpdateInput } from '../types'
import type { PaginatedApInvoices } from '../view-models'
import { formatInvoicePriority, formatInvoiceStatus } from '../status'
import { getApiErrorMessage } from '@/lib/api-error'

export function useInvoiceListBulk(
  data: PaginatedApInvoices | undefined,
  staff: ApInvoiceAssignedUser[],
) {
  const [selectedInvoiceUuids, setSelectedInvoiceUuids] = useState<string[]>([])
  const [bulkStatus, setBulkStatus] = useState('keep')
  const [bulkPriority, setBulkPriority] = useState('keep')
  const [bulkAssignee, setBulkAssignee] = useState<'keep' | 'clear' | `staff:${number}`>('keep')
  const [bulkMarkPaid, setBulkMarkPaid] = useState(false)
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false)
  const bulkUpdateInvoices = useBulkUpdateInvoices()

  const visibleInvoiceUuids = useMemo(
    () => (data?.items ?? []).map((invoice) => invoice.uuid).filter(Boolean) as string[],
    [data?.items],
  )
  const selectedVisibleCount = visibleInvoiceUuids.filter((uuid) =>
    selectedInvoiceUuids.includes(uuid),
  ).length
  const allVisibleSelected =
    visibleInvoiceUuids.length > 0 && selectedVisibleCount === visibleInvoiceUuids.length
  const selectedVisibleRows = useMemo(
    () =>
      (data?.items ?? []).filter(
        (invoice) => invoice.uuid && selectedInvoiceUuids.includes(invoice.uuid),
      ),
    [data?.items, selectedInvoiceUuids],
  )
  const selectedVisibleTotal = selectedVisibleRows.reduce(
    (total, invoice) => total + Number(invoice.invoice_total || 0),
    0,
  )
  const bulkPatch = useMemo(() => {
    const patch: Omit<ApInvoiceBulkUpdateInput, 'uuids'> = {}
    if (bulkMarkPaid) patch.mark_paid_in_full = true
    if (!bulkMarkPaid && bulkStatus !== 'keep') patch.status = bulkStatus
    if (!bulkMarkPaid && bulkPriority !== 'keep') patch.priority_status = bulkPriority
    if (bulkAssignee === 'clear') patch.assigned_user_id = null
    if (bulkAssignee.startsWith('staff:')) {
      patch.assigned_user_id = Number(bulkAssignee.replace('staff:', ''))
    }
    return patch
  }, [bulkAssignee, bulkMarkPaid, bulkPriority, bulkStatus])
  const canApplyBulkUpdate =
    selectedInvoiceUuids.length > 0 &&
    Object.keys(bulkPatch).length > 0 &&
    !bulkUpdateInvoices.isPending
  const bulkChangeLabels = useMemo(() => {
    const labels: string[] = []
    if (bulkMarkPaid) labels.push('record remaining paid-in-full evidence')
    if (!bulkMarkPaid && bulkStatus !== 'keep') {
      labels.push(`status → ${formatInvoiceStatus(bulkStatus)}`)
    }
    if (!bulkMarkPaid && bulkPriority !== 'keep') {
      labels.push(`priority → ${formatInvoicePriority(bulkPriority)}`)
    }
    if (bulkAssignee === 'clear') labels.push('assignee → unassigned')
    if (bulkAssignee.startsWith('staff:')) {
      const staffId = Number(bulkAssignee.replace('staff:', ''))
      labels.push(
        `assignee → ${staff.find((user) => user.id === staffId)?.name || 'selected staff'}`,
      )
    }
    return labels
  }, [bulkAssignee, bulkMarkPaid, bulkPriority, bulkStatus, staff])

  const setVisibleSelection = (checked: boolean) => {
    setSelectedInvoiceUuids((current) => {
      if (!checked) return current.filter((uuid) => !visibleInvoiceUuids.includes(uuid))
      return Array.from(new Set([...current, ...visibleInvoiceUuids]))
    })
  }

  const setRowSelection = (uuid: string | null | undefined, checked: boolean) => {
    if (!uuid) return
    setSelectedInvoiceUuids((current) => {
      if (!checked) return current.filter((selectedUuid) => selectedUuid !== uuid)
      return current.includes(uuid) ? current : [...current, uuid]
    })
  }

  const resetBulkControls = () => {
    setBulkStatus('keep')
    setBulkPriority('keep')
    setBulkAssignee('keep')
    setBulkMarkPaid(false)
  }

  const applyBulkUpdates = () => {
    setBulkConfirmOpen(false)
    bulkUpdateInvoices.mutate(
      { uuids: selectedInvoiceUuids, ...bulkPatch },
      {
        onSuccess: ({ updated, failed }) => {
          if (updated.length) {
            toast.success(`Updated ${updated.length} invoice${updated.length === 1 ? '' : 's'}`)
          }
          for (const failure of failed) {
            toast.error(`Invoice ${failure.uuid}: ${failure.reason}`)
          }
          if (!updated.length && failed.length) {
            toast.error('No selected invoices were updated')
          }
          setSelectedInvoiceUuids(failed.map((failure) => failure.uuid))
          if (!failed.length) resetBulkControls()
          setBulkConfirmOpen(false)
        },
        onError: (err) =>
          toast.error(getApiErrorMessage(err, 'Bulk invoice update failed')),
      },
    )
  }

  return {
    selectedInvoiceUuids,
    setSelectedInvoiceUuids,
    allVisibleSelected,
    setVisibleSelection,
    setRowSelection,
    selectedVisibleRows,
    selectedVisibleTotal,
    bulkStatus,
    setBulkStatus,
    bulkPriority,
    setBulkPriority,
    bulkAssignee,
    setBulkAssignee,
    bulkMarkPaid,
    setBulkMarkPaid,
    bulkConfirmOpen,
    setBulkConfirmOpen,
    bulkUpdateInvoices,
    bulkPatch,
    canApplyBulkUpdate,
    bulkChangeLabels,
    resetBulkControls,
    applyBulkUpdates,
  }
}
